import { Component, Input, OnInit } from "@angular/core";
import { AbstractControl, FormGroup, Validators } from "@angular/forms";
import { MessagingService } from "../../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../../core/messaging/severity-type.enum";
import { FormService } from "../../../../../dynamic-forms/form.service";
import { Dropdown } from "../../../../../dynamic-forms/inputs/dropdown/dropdown.model";
import { DynamicInput } from "../../../../../dynamic-forms/inputs/dynamic-input.model";
import { SelectableInput } from "../../../../../dynamic-forms/inputs/selectable-input.model";
import { Switch } from "../../../../../dynamic-forms/inputs/switch/switch.model";
import { TextboxType } from "../../../../../dynamic-forms/inputs/textbox/textbox-type.enum";
import { Textbox } from "../../../../../dynamic-forms/inputs/textbox/textbox.model";
import { ArrayHelper } from "../../../../../utilities/contracts/array-helper";
import { StringHelper } from "../../../../../utilities/contracts/string-helper";
import { RegExHelper } from "../../../../../utilities/reg-Ex-Helper";
import { Measures } from "../models/measures.model";
import { IDisabledRateTypes, IOverreadModel, RateType } from "../models/overread.model";
import { IRateModel } from "../models/rates.model";

interface IMeasureForm {
  formGroup: FormGroup;
  models: DynamicInput[];
}

interface IMeasureRowSnapshot {
  measure: string;
  or1: { [key: string]: number };
  or2: { [key: string]: number };
}

enum MeasureInputs {
  MEASURE = "measure",
}

@Component({
             selector: "app-measure-sampling-overread",
             templateUrl: "./measure-sampling-overread.component.html",
             styleUrls: ["./measure-sampling-overread.component.scss"],
           })
export class MeasureSamplingOverreadComponent implements OnInit {

  @Input() set projectSamplingActive(value: boolean) {
    this.projectSamplingActiveValue = value;
    if (!value) {
      this.measureSamplingEnabled = false;
      this.measureSamplingSwitchGroup?.patchValue({active: false});
    }
  }

  get projectSamplingActive(): boolean {
    return this.projectSamplingActiveValue;
  }

  private projectSamplingActiveValue = false;

  @Input() set measureList(list: Measures[]) {
    this.measureOptions = [...list.map(i => new SelectableInput({text: i.measureCode, value: i.measureCode}))];
  }

  @Input() set disabledRateTypes(value: IDisabledRateTypes) {
    this.disabledRateTypesValue = value ?? { OR1: [], OR2: [] };
    this.applyDisabledRateTypes();
  }

  @Input() set overreadInfo(info: IOverreadModel) {
    if (!ArrayHelper.isAvailable(info?.overread?.rates?.rate) || !ArrayHelper.isAvailable(info?.overread2?.rates?.rate)) {
      this.buildRow();
      return;
    }
    this.measureFormOverread = [];
    this.measureFormOverread2 = [];
    const overread = this.filterByMeasureCode(info.overread.rates.rate);
    const overread2 = this.filterByMeasureCode(info.overread2.rates.rate);
    const groupedByMeasureCodeOR1 = this.groupByProperty(overread, "measurecode");
    const groupedByMeasureCodeOR2 = this.groupByProperty(overread2, "measurecode");
    this.createRows(groupedByMeasureCodeOR1, groupedByMeasureCodeOR2);
    this.savedSnapshots = this.measureFormOverread.map((_, index) => this.captureSnapshot(index));

    // no dedicated persisted flag; derived from any measure-level row's active flag
    this.measureSamplingEnabled = [...overread, ...overread2].some(rate => rate.active === 1);
    this.measureSamplingSwitchGroup?.patchValue({active: this.measureSamplingEnabled});
  }

  measureFormOverread: IMeasureForm[] = [];
  measureFormOverread2: IMeasureForm[] = [];
  measureOptions: SelectableInput[] = [];

  measureSamplingSwitchGroup: FormGroup;
  measureSamplingActiveSwitch = new Switch({key: "active", label: ""});
  measureSamplingEnabled = false;

  isConfirmDeleteVisible = false;
  private pendingDeleteIndex: number = null;

  private savedSnapshots: (IMeasureRowSnapshot | null)[] = [];
  private disabledRateTypesValue: IDisabledRateTypes = { OR1: [], OR2: [] };
  // "stage:ratetype" keys currently over 100%, cleared one column at a time as the user edits it
  private breachingColumns = new Set<string>();

  constructor(
    private readonly formService: FormService,
    private messagingService: MessagingService) {
  }

  ngOnInit(): void {
    this.measureSamplingSwitchGroup = this.formService.createFormGroup([this.measureSamplingActiveSwitch]);
    this.measureSamplingSwitchGroup.patchValue({active: this.measureSamplingEnabled});
  }

  onMeasureSamplingSwitchChange(event: { checked: boolean }): void {
    if (event && event.checked !== undefined) {
      this.measureSamplingEnabled = event.checked;
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  addMeasure(): void {
    this.buildRow();
  }

  getControls(form: FormGroup): AbstractControl[] {
    const controls: AbstractControl[] = [];
    for (const [key, control] of Object.entries(form.controls)) {
      controls.push(control);
    }
    return controls;
  }

  confirmDeleteRow(index: number): void {
    this.pendingDeleteIndex = index;
    this.isConfirmDeleteVisible = true;
  }

  deleteRow(): void {
    if (this.pendingDeleteIndex === null) {
      return;
    }
    this.measureFormOverread.splice(this.pendingDeleteIndex, 1);
    this.measureFormOverread2.splice(this.pendingDeleteIndex, 1);
    this.savedSnapshots.splice(this.pendingDeleteIndex, 1);
    this.pendingDeleteIndex = null;
    // counts as editing every column
    this.breachingColumns.clear();
  }

  revertValues(index: number): void {
    const snapshot = this.savedSnapshots[index];
    if (!snapshot) {
      this.measureFormOverread[index].formGroup.reset();
      this.measureFormOverread2[index].formGroup.reset();
    } else {
      this.measureFormOverread[index].formGroup.patchValue({measure: snapshot.measure, ...snapshot.or1});
      this.measureFormOverread2[index].formGroup.patchValue({...snapshot.or2});
    }
    // counts as editing every column
    this.breachingColumns.clear();
  }

  // (onChange) only fires on blur; column errors need to clear on the first keystroke, so this is wired to (onInput) instead
  onRateFieldInput(stage: "OR1" | "OR2", key: string): void {
    this.breachingColumns.delete(`${stage}:${key}`);
  }

  onInputChange(event: { value: string }): void {
    if (!StringHelper.isAvailable(event?.value)) {
      return;
    }
    if (this.hasDuplicates(this.measureFormOverread, event.value)) {
      this.messagingService.showToast("Measures cannot have duplicates", SeverityType.ERROR);
    }
  }

  formHasErrors(): boolean {
    return this.measureFormOverread.some(form => form.formGroup.invalid) || this.measureFormOverread2.some(form => form.formGroup.invalid);
  }

  formHasDuplicates(): boolean {
    const measures = this.measureFormOverread
      .map(m => m.formGroup.get(MeasureInputs.MEASURE)?.value as string)
      .filter(value => StringHelper.isAvailable(value));
    const duplicates = new Set(measures);
    return measures.length !== duplicates.size;
  }

  // recomputes every column from scratch; cleared-by-edit state doesn't carry over
  formHasColumnErrors(): boolean {
    this.breachingColumns.clear();
    Object.values(RateType).forEach(ratetype => {
      this.checkColumnSum("OR1", ratetype, this.measureFormOverread);
      this.checkColumnSum("OR2", ratetype, this.measureFormOverread2);
    });
    return this.breachingColumns.size > 0;
  }

  // a breaching column can be disabled after the fact; only count ones still actually highlighted
  get hasColumnErrors(): boolean {
    return (["OR1", "OR2"] as const).some(stage => Object.values(RateType).some(ratetype => this.isColumnInError(stage, ratetype)));
  }

  isColumnInError(stage: "OR1" | "OR2", ratetype: RateType): boolean {
    if (this.isRateTypeDisabled(stage, ratetype)) {
      return false;
    }
    return this.breachingColumns.has(`${stage}:${ratetype}`);
  }

  getValuesAsObject(type: "OR1" | "OR2" = "OR1"): IRateModel[] {
    const rates: IRateModel[] = [];
    // subset of Project Sampling: inactive whenever Project Sampling is off
    const active = this.projectSamplingActive && this.measureSamplingEnabled ? 1 : 0;
    this.measureFormOverread.forEach((_, index) => {
      const measurecode = this.measureFormOverread[index].formGroup.get(MeasureInputs.MEASURE)?.value as string;
      if (!StringHelper.isAvailable(measurecode)) {
        return;
      }
      const sourceForm = type === "OR1" ? this.measureFormOverread[index] : this.measureFormOverread2[index];
      Object.values(RateType).forEach(input => {
        const value = sourceForm.formGroup.get(input)?.value;
        const isBlank = value === null || value === undefined || value === "";
        if (isBlank) {
          return;
        }
        rates.push({
                     ratetype: input,
                     value: Number(value),
                     measurecode,
                     active,
                   });
      });
    });
    return rates;
  }

  commitSnapshot(): void {
    this.savedSnapshots = this.measureFormOverread.map((form, index) => {
      const measure = form.formGroup.get(MeasureInputs.MEASURE)?.value as string;
      return StringHelper.isAvailable(measure) ? this.captureSnapshot(index) : null;
    });
  }

  private applyDisabledRateTypes(): void {
    this.measureFormOverread.forEach(row => this.applyToRow(row, this.disabledRateTypesValue.OR1));
    this.measureFormOverread2.forEach(row => this.applyToRow(row, this.disabledRateTypesValue.OR2));
  }

  // a disabled bucket doesn't exist at the project level for that stage, so it's excluded entirely
  private checkColumnSum(stage: "OR1" | "OR2", ratetype: RateType, rows: IMeasureForm[]): void {
    if (this.isRateTypeDisabled(stage, ratetype)) {
      return;
    }
    const sum = rows.reduce((total, row, index) => {
      const measurecode = this.measureFormOverread[index]?.formGroup.get(MeasureInputs.MEASURE)?.value as string;
      if (!StringHelper.isAvailable(measurecode)) {
        return total; // dropped silently on save; doesn't count toward the column
      }
      const value = row.formGroup.get(ratetype)?.value;
      const isBlank = value === null || value === undefined || value === "";
      return isBlank ? total : total + Number(value);
    },                      0);
    if (sum > 100) {
      this.breachingColumns.add(`${stage}:${ratetype}`);
    }
  }

  private isRateTypeDisabled(stage: "OR1" | "OR2", ratetype: RateType): boolean {
    return this.disabledRateTypesValue[stage]?.includes(ratetype) ?? false;
  }

  private applyToRow(row: IMeasureForm, disabled: RateType[]): void {
    Object.values(RateType).forEach(ratetype => {
      // createRows only builds controls for ratetypes present in the payload, so a row can be missing one
      const control = row.formGroup.get(ratetype);
      if (control == null) {
        return;
      }
      if (disabled.includes(ratetype)) {
        control.disable({ emitEvent: false });
        control.reset(); // clear the input
        return;
      }
      control.enable({ emitEvent: false });
    });
  }

  private captureSnapshot(index: number): IMeasureRowSnapshot {
    return {
      measure: this.measureFormOverread[index]?.formGroup.get(MeasureInputs.MEASURE)?.value as string,
      // getRawValue, not value: disabled controls are omitted from FormGroup.value and would be lost on revert
      or1: {...this.measureFormOverread[index]?.formGroup.getRawValue()},
      or2: {...this.measureFormOverread2[index]?.formGroup.getRawValue()},
    };
  }

  private buildRow(): void {
    const models = [
      this.createDropdown("measure", ""),
      this.createTextbox("compliant", ""),
      this.createTextbox("partiallycompliant", ""),
      this.createTextbox("noncompliant", ""),
      this.createTextbox("noncompliantexclusion", ""),
    ];
    const modelsOverread2 = [
      this.createTextbox("compliant", ""),
      this.createTextbox("partiallycompliant", ""),
      this.createTextbox("noncompliant", ""),
      this.createTextbox("noncompliantexclusion", ""),
    ];
    const or1Row: IMeasureForm = {formGroup: this.formService.createFormGroup(models), models};
    const or2Row: IMeasureForm = {
      formGroup: this.formService.createFormGroup(modelsOverread2),
      models: modelsOverread2,
    };
    this.applyToRow(or1Row, this.disabledRateTypesValue.OR1);
    this.applyToRow(or2Row, this.disabledRateTypesValue.OR2);
    this.measureFormOverread = [...this.measureFormOverread, or1Row];
    this.measureFormOverread2 = [...this.measureFormOverread2, or2Row];
    this.savedSnapshots = [...this.savedSnapshots, null];
  }

  private createTextbox(key: string, label: string, value?: number): Textbox {
    return new Textbox(
      {
        key,
        label,
        value,
        validators: [Validators.min(0), Validators.max(100), Validators.pattern(RegExHelper.wholeNumber)],
        errorMessages: {
          min: "Enter 0 - 100.",
          max: "Enter 0 - 100.",
          pattern: "Enter 0 - 100.",
        },
      });
  }

  private createDropdown(key: string, label: string, value?: string): Dropdown {
    return new Dropdown(
      {
        key,
        label,
        value,
        options: this.measureOptions,
        placeholder: "Measure...",
      });
  }

  private groupByProperty(arr: IRateModel[], prop: string): {[key: string]: IRateModel[]} {
    return arr.reduce((acc, currentItem) => {
      const key = currentItem[prop];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(currentItem);
      return acc;
    },                {});
  }

  // build one row per measure across BOTH stages, even if a stage has no rates for it
  // (a measure with all 4 fields blank in a stage is omitted from the API response there)
  private createRows(groupedOR1: {[key: string]: IRateModel[]}, groupedOR2: {[key: string]: IRateModel[]}): void {
    const measureCodes = [...new Set([...Object.keys(groupedOR1), ...Object.keys(groupedOR2)])];
    measureCodes.forEach(measurecode => {
      const or1Row = this.buildMeasureRow("OR1", measurecode, groupedOR1[measurecode]);
      const or2Row = this.buildMeasureRow("OR2", measurecode, groupedOR2[measurecode]);
      this.applyToRow(or1Row, this.disabledRateTypesValue.OR1);
      this.applyToRow(or2Row, this.disabledRateTypesValue.OR2);
      this.measureFormOverread = [...this.measureFormOverread, or1Row];
      this.measureFormOverread2 = [...this.measureFormOverread2, or2Row];
    });
  }

  // always creates all 4 ratetype controls, patching only the ones the API actually returned
  private buildMeasureRow(type: "OR1" | "OR2", measurecode: string, rates: IRateModel[] = []): IMeasureForm {
    const models: DynamicInput[] = [];
    const valuesToPatch: {[key: string]: string | number} = {};
    if (type === "OR1") {
      models.push(this.createDropdown(MeasureInputs.MEASURE, ""));
      valuesToPatch[MeasureInputs.MEASURE] = measurecode;
    }
    const valueByRatetype = new Map(rates.map(rate => [rate.ratetype, rate.value]));
    Object.values(RateType).forEach(ratetype => {
      models.push(this.createTextbox(ratetype, ""));
      if (valueByRatetype.has(ratetype)) {
        valuesToPatch[ratetype] = valueByRatetype.get(ratetype);
      }
    });
    const formGroup = this.formService.createFormGroup(models);
    formGroup.patchValue(valuesToPatch);
    return {formGroup, models};
  }

  private filterByMeasureCode(arr: IRateModel[]): IRateModel[] {
    return  arr.filter(value => StringHelper.isAvailable(value?.measurecode) && this.measureOptions?.find(m => m.text === value?.measurecode));
  }

  private hasDuplicates(arr: IMeasureForm[], value: string): boolean {
    const findings = arr.filter(measureForm => measureForm.formGroup.get(MeasureInputs.MEASURE)?.value === value);
    return ArrayHelper.isAvailable(findings) && findings.length > 1;
  }

}
